<template>
  <div>

    <div v-if="localSetting && localSetting.db" :class="'setting ' + (this.loading ? ' setting--loading' : '') + (!localSetting.db.in_use ? ' setting--not-in-use' : '') + (!localSetting.can_edit ? ' setting--disabled' : '')">
      <div class="setting__title">
        <img class="setting__icon" src="https://www.branas.se/app/themes/branasgruppen-core/assets/images/weather/sun.svg" v-if="localSetting.raw && localSetting.raw.season && localSetting.raw.season === 'summer'" />
        <img class="setting__icon" src="https://www.branas.se/app/themes/branasgruppen-core/assets/images/icons/snowflake-icon.svg" v-if="localSetting.raw && localSetting.raw.season && localSetting.raw.season === 'winter'" />

        <span class="setting__active">
          <input type="checkbox" id="checkbox" v-model="localSetting.db.in_use" @change="updateSetting(localSetting.db)">
        </span>

        {{localSetting.title || localSetting.db.key}}

        <div class="setting__description">
          <div v-if="localSetting.db.description">{{localSetting.db.description}}</div>
        </div>

        <div class="setting__details">
          <div>Tillåter HTML: <span>{{ localSetting.html ? '✓' : '⨉' }}</span></div>
          <div v-if="localSetting.db.example">Exempel: <span v-html="localSetting.db.example"></span></div>
          <div v-if="localSetting.db.default_value">Standard: {{localSetting.db.default_value}}</div>
          <div v-if="localSetting.db.updated_at">Senast ändrad: {{localSetting.db.updated_at | fromNow}}</div>
        </div>
      </div>
      <div v-if="localSetting.db.in_use" :class="'setting__editor setting__editor--' + (localSetting.db.key.replace('.', '-'))">
        <div v-if="setting.editor === 'boolean'">
          <div style="width: 105px;">
            <SwitchButton @toggle="toggle" :store="localSetting.db.key" :iconscale="2" :value="localSetting.db.value" :sync="false" :labels="{ checked: 'Ja', unchecked: 'Nej'}" />
          </div>
        </div>
        <div v-else-if="setting.editor === 'date'">
          <input type="date" @change="updateSetting(localSetting.db)" v-model="localSetting.db.value">
        </div>
        <div v-else-if="setting.editor === 'tablelist'">
          <div style="margin-bottom: 0.75rem; display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap;">
            <button v-if="!sortingEnabled && localSetting.db.value.length > 2 && localSetting.edit_settings.sortable" class="button small white border" style="padding: 4px 10px; font-size: 0.85em;" @click.stop.prevent="enableSorting">{{ 'Ändra ordning' }}</button>
            <button v-if="sortingEnabled" class="small green" style="padding: 4px 10px; font-size: 0.85em;" @click.stop.prevent="saveSorting">{{ 'Spara' }}</button>
            <button v-if="sortingEnabled" class="small border" style="padding: 4px 10px; font-size: 0.85em;" @click.stop.prevent="cancelSorting">{{ 'Avbryt' }}</button>
          </div>

          <table :class="'multi-value-table multi-value-table--' + localSetting.edit_settings.columns + (sortingEnabled ? ' sorting-active' : '')">
            <tbody>
              <tr v-for="(row, rowIndex) in localSetting.db.value" 
                  :key="localSetting.db.key + '-' + rowIndex" 
                  :class="(rowIndex === localSetting.db.value.length - 1 ? 'multi-value-table--last' : '') + (sortingEnabled ? ' draggable-row' : '') + (sortingEnabled && dragOverIndex === rowIndex ? ' drag-over' : '')"
                  :draggable="sortingEnabled && rowIndex < localSetting.db.value.length - 1"
                  @dragstart="dragStart($event, rowIndex)"
                  @dragenter="dragEnter(rowIndex)"
                  @dragend="dragEnd"
                  @dragover.prevent
                  @drop="dropRow($event, rowIndex)">
                <td v-for="(col, colIndex) in row" :key="colIndex" v-show="!localSetting.edit_settings.column_types || localSetting.edit_settings.column_types[colIndex] !== 'hidden'">
                  <div v-if="localSetting.edit_settings.column_types && localSetting.edit_settings.column_types[colIndex] === 'singleline'">
                    <input type="text" :disabled="sortingEnabled" @change="updateSetting(localSetting.db)" v-model="localSetting.db.value[rowIndex][colIndex]" :placeholder="localSetting.edit_settings.column_help && localSetting.edit_settings.column_help[colIndex] || 'Värde'" />
                  </div>
                  <div v-if="localSetting.edit_settings.column_types && localSetting.edit_settings.column_types[colIndex] === 'id'">
                    #{{ localSetting.db.value[rowIndex][colIndex] }}
                  </div>
                  <div v-if="localSetting.edit_settings.column_types && localSetting.edit_settings.column_types[colIndex] === 'text'">
                    <textarea :disabled="sortingEnabled" @change="updateSetting(localSetting.db)" v-model="localSetting.db.value[rowIndex][colIndex]" :placeholder="localSetting.edit_settings.column_help && localSetting.edit_settings.column_help[colIndex] || 'Värde'"></textarea>
                  </div>
                </td>
                <td class="multi-value-table--counter">
                  <span v-if="sortingEnabled && rowIndex < localSetting.db.value.length - 1" style="font-size: 1.1em; cursor: grab;">☰</span>
                  <span v-else-if="rowIndex === localSetting.db.value.length - 1">Ny</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else-if="setting.editor === 'translator'">
          <table :class="'multi-value-table multi-value-table--' + SITE_CONFIG.languages.full.length">
            <tbody>
              <tr>
                <td v-for="(lang, langIndex) in SITE_CONFIG.languages.full" :key="langIndex">
                  <label>
                    <img :src="'https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.2.0/flags/1x1/' + lang.flag + '.svg'" width="12" />
                    {{lang.title}}
                  </label>
                  <textarea @change="updateSetting(localSetting.db)" v-model="localSetting.db.value[lang.value]"></textarea>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else>
          <textarea @change="updateSetting(localSetting.db)" v-model="localSetting.db.value" placeholder="Värde"></textarea>
        </div>
        <div v-if="setting.editor === 'image'" class="image-preview">
          <a :href="localSetting.db.value" target="_blank"><img v-if="localSetting.db.value" :src="localSetting.db.value" /></a>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import API from '@/classes/API';
import moment from 'moment';
import SwitchButton from '@/components/components/SwitchButton';

export default {
  components: {
    SwitchButton,
  },
  props: ['setting'],
  data() {
    return {
      localSetting: [],
      loading: false,
      expanded: false,
      sortingEnabled: false,
      originalValueBackup: null,
      draggedIndex: null,
      dragOverIndex: null,
    };
  },
  mounted() {
    this.localSetting = Object.assign({}, this.setting);
    this.transformSetting();
  },
  methods: {
    async updateSetting(setting) {
      this.loading = true;

      try {
        const { data } = await API.post(`/admin/setting/${setting.key}`, {
          data: setting.value,
          in_use: setting.in_use,
        });

        this.localSetting.db.value = data;
        this.localSetting.db.updated_at = moment().format('YYYY-MM-DD HH:mm:ss');
        this.transformSetting();

        this.$toast.success('Inställningen är sparad', 'Sparat', {
          position: 'topRight',
        });
      } catch (e) {
        this.$toast.error('Inställningen sparades inte', 'Fel', {
          position: 'topRight',
        });
      }


      this.loading = false;
    },
    transformSetting() {
      if (this.localSetting.editor === 'boolean') {
        this.localSetting.db.value = this.localSetting.db.value === '1' || this.localSetting.db.value === true;
      }
    },
    toggle(e) {
      this.localSetting.db.value = e.value ? 1 : 0;
      this.updateSetting(this.localSetting.db);
    },
    enableSorting() {
      this.originalValueBackup = JSON.parse(JSON.stringify(this.localSetting.db.value));
      this.sortingEnabled = true;
    },
    saveSorting() {
      this.sortingEnabled = false;
      this.originalValueBackup = null;
      this.updateSetting(this.localSetting.db);
    },
    cancelSorting() {
      this.localSetting.db.value = this.originalValueBackup;
      this.sortingEnabled = false;
      this.originalValueBackup = null;
    },
    dragStart(event, index) {
      if (!this.sortingEnabled) return;
      this.draggedIndex = index;
      // eslint-disable-next-line no-param-reassign
      event.dataTransfer.effectAllowed = 'move';
    },
    dragEnter(index) {
      if (!this.sortingEnabled) return;
      const lastIndex = this.localSetting.db.value.length - 1;
      if (index < lastIndex) {
        this.dragOverIndex = index;
      }
    },
    dragEnd() {
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },
    dropRow(event, index) {
      if (!this.sortingEnabled) return;
      const lastIndex = this.localSetting.db.value.length - 1;
      if (index >= lastIndex || this.draggedIndex === index) {
        this.dragOverIndex = null;
        this.draggedIndex = null;
        return;
      }

      const draggedRow = this.localSetting.db.value[this.draggedIndex];
      this.localSetting.db.value.splice(this.draggedIndex, 1);
      this.localSetting.db.value.splice(index, 0, draggedRow);
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },
  },
};
</script>

<style scoped lang="stylus">
input
  pading 2px 5px

.setting
  display flex
  margin-bottom 0.2em
  padding 20px 40px
  padding-left 60px
  flex-wrap: wrap
  &--not-in-use
    position relative
    div
      opacity 0.5
      filter grayscale(100%)
    &:after
      content "Ej aktiv"
      position absolute
      top 15px
      right 40px
      color gold
      font-size 24px
      font-weight bold
      text-transform uppercase
      text-align center
  &--disabled
    position relative
    pointer-events none
    user-select none
    div
      opacity 0.5
      filter grayscale(100%)
    &:after
      content "Ej redigerbar"
      position absolute
      top 15px
      right 40px
      color black
      font-size 24px
      font-weight bold
      text-transform uppercase
      text-align center
  &--loading
    opacity 0.2
    filter grayscale(100%)
    pointer-events: none
  &__title
    font-weight bold
    font-size 16px
    width 40%
    padding-right 20px
    word-break: break-word
    position: relative
  &__active
    position absolute
    left -20px
  &__icon
    position absolute
    left -50px
    width 20px
    height 20px
  &__details
    margin-top 0.4em
    width 100%
    font-weight 300
    font-size 14px
    font-style italic
    word-break: break-all
  &__description
    font-weight 300
    font-size 14px
    margin-top 0.5em
  &__editor
    width 60%
    padding-left 20px
    word-break: break-all

textarea,
input[type="text"],
input[type="date"]
  border-radius 2px
  border 0
  padding 12px 15px
  line-height 1.3em
  &:focus
    outline 1px solid #999999
    color #000000

textarea
  width 100%

.image-preview
  padding 10px
  margin-top 10px
  margin-bottom 10px
  background: repeating-linear-gradient(
    45deg,
    #bbbbbb,
    #bbbbbb 10px,
    #dddddd 10px,
    #dddddd 20px
  );
  text-align: center
  img
    max-width 100%
    max-height 75px
    width: 100%
    height auto
    object-fit: contain

.advanced-table
  width 100%
  & > tr > td 
    padding-bottom 12px
    label
      display flex
      margin-top 4px
      span
        width 40%

.multi-value-table
  table-layout fixed
  width 100%
  tr.multi-value-table--last
    opacity 0.5
  tr.multi-value-table--last:focus-within
    opacity 1
  td 
    padding-right 0.75rem
    padding-bottom 0.25rem
    vertical-align top
    &.multi-value-table--counter
      width 3rem

.draggable-row
  cursor grab
  &:active
    cursor grabbing
  &:hover
    background-color #f9f9f9

.sorting-active
  opacity 0.95
  tr.multi-value-table--last
    display none

.drag-over
  background-color #e3f2fd !important
  outline 2px dashed #005580
</style>



// WEBPACK FOOTER //
// Setting.vue?0680b7a8